import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Track } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Search, Save, Download, Upload, Filter, ArrowUpDown, Check, Music } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

// Define comprehensive mood lists based on categories
const moodCategories = {
  "Positive/Uplifting": {
    "Happy": ["Cheerful", "Joyful", "Upbeat", "Optimistic", "Bright", "Carefree"],
    "Inspirational": ["Motivational", "Uplifting", "Encouraging", "Hopeful", "Empowering"],
    "Playful": ["Fun", "Quirky", "Lighthearted", "Whimsical", "Humorous", "Silly"],
    "Excited": ["Energetic", "Enthusiastic", "Exhilarating", "Thrilling"],
    "Celebratory": ["Triumphant", "Victorious", "Anthemic", "Euphoric", "Festive"]
  },
  "Calm/Peaceful": {
    "Relaxed": ["Chilled", "Laid-back", "Easygoing", "Mellow", "Smooth"],
    "Peaceful": ["Serene", "Tranquil", "Calm", "Gentle", "Soothing"],
    "Meditative": ["Reflective", "Contemplative", "Mindful", "Zen"],
    "Dreamy": ["Ethereal", "Floating", "Airy", "Ambient", "Atmospheric"],
    "Content": ["Satisfied", "Comforted", "Cozy", "Warm"]
  },
  "Somber/Emotional": {
    "Sad": ["Melancholic", "Sorrowful", "Wistful", "Mournful", "Heartbreaking"],
    "Nostalgic": ["Reminiscent", "Sentimental", "Yearning", "Bittersweet"],
    "Emotional": ["Moving", "Touching", "Poignant", "Stirring", "Passionate"],
    "Vulnerable": ["Delicate", "Fragile", "Tender", "Intimate", "Sensitive"],
    "Lonely": ["Isolated", "Distant", "Longing", "Desolate", "Empty"]
  },
  "Intense/Dynamic": {
    "Dramatic": ["Powerful", "Impactful", "Bold", "Intense", "Epic"],
    "Tense": ["Suspenseful", "Anxious", "Uneasy", "Nervous", "Edgy"],
    "Mysterious": ["Enigmatic", "Intriguing", "Curious", "Cryptic", "Secretive"],
    "Dark": ["Ominous", "Brooding", "Foreboding", "Sinister", "Gloomy"],
    "Aggressive": ["Angry", "Fierce", "Furious", "Hostile", "Violent"]
  },
  "Focused/Purposeful": {
    "Determined": ["Resolute", "Persistent", "Focused", "Driven"],
    "Confident": ["Assured", "Self-assured", "Poised", "Bold"],
    "Serious": ["Earnest", "Solemn", "Grave", "Weighty"],
    "Corporate": ["Professional", "Business-like", "Formal", "Clean"],
    "Sophisticated": ["Elegant", "Refined", "Classy", "Polished"]
  },
  "Descriptive/Atmospheric": {
    "Cinematic": ["Orchestral", "Soundtrack", "Filmic", "Grand"],
    "Futuristic": ["Technological", "Sci-fi", "Innovative", "Modern"],
    "Retro": ["Vintage", "Old-school", "Nostalgic", "Classic"],
    "Ethereal": ["Magical", "Enchanting", "Mystical", "Otherworldly"],
    "Urban": ["Street", "Gritty", "Metropolitan", "City"]
  }
};

// Create flat mood lists for dropdowns
const primaryMoods: string[] = [];
const specificMoods: string[] = [];

// Populate mood arrays
Object.keys(moodCategories).forEach(category => {
  Object.entries(moodCategories[category as keyof typeof moodCategories]).forEach(([primary, specifics]) => {
    primaryMoods.push(primary);
    specifics.forEach(specific => specificMoods.push(specific));
  });
});

// Sort the mood arrays alphabetically
primaryMoods.sort();
specificMoods.sort();

// All moods in a single flat array for simple dropdown
const allMoods = [...primaryMoods, ...specificMoods].sort();

type TrackWithMood = Track & {
  updatedMood?: string;
  primaryMood?: string;
  specificMood?: string;
  originalMood?: string;
  visible?: boolean;
};

export default function MoodEditor() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [updatedTracks, setUpdatedTracks] = useState<Record<number, string>>({});
  const [primaryMoodSelections, setPrimaryMoodSelections] = useState<Record<number, string>>({});
  const [specificMoodSelections, setSpecificMoodSelections] = useState<Record<number, string>>({});
  const [visibilitySelections, setVisibilitySelections] = useState<Record<number, boolean>>({});
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [sortField, setSortField] = useState<'title' | 'bpm' | 'mood'>('title');
  const [filterByUpdated, setFilterByUpdated] = useState<boolean>(false);
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  
  // Fetch tracks using useQuery (will initially get limited results or cached data)
  const { data, isLoading, error, refetch } = useQuery<{ 
    tracks: Track[],
    pagination: {
      total: number,
      page: number,
      limit: number,
      totalPages: number
    }
  }>({ 
    queryKey: ['/api/tracks', { limit: 1000 }], // Consistent query key
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Allow cached data initially
  });

  // Direct fetch on mount to ensure all tracks are loaded
  useEffect(() => {
    console.log('[MoodEditor] INITIAL MOUNT - Forcing fetch all tracks');
    fetch('/api/tracks?limit=1000')
      .then(response => response.json())
      .then(fetchedData => {
        if (fetchedData?.tracks) {
          console.log(`[MoodEditor] Direct fetch successful, got ${fetchedData.tracks.length} tracks.`);
          // Update the react-query cache with the full data
          queryClient.setQueryData(['/api/tracks', { limit: 1000 }], fetchedData);
          toast({
            title: "All Tracks Loaded",
            description: `Loaded ${fetchedData.tracks.length} tracks into editor.`,
          });
        } else {
          throw new Error('Invalid data format from direct fetch');
        }
      })
      .catch(err => {
        console.error('[MoodEditor] Error during direct fetch:', err);
        toast({
          title: "Error Loading All Tracks",
          description: "Failed to fetch the full track list. Please try refreshing.",
          variant: "destructive",
        });
      });
  }, [queryClient]); // Dependency on queryClient

  // Function to handle mood updates
  const handlePrimaryMoodChange = (trackId: number, mood: string) => {
    // If mood is empty or default, clear the selection
    if (!mood) {
      setPrimaryMoodSelections(prev => {
        const newSelections = { ...prev };
        delete newSelections[trackId];
        return newSelections;
      });
      
      // Also clear the specific mood
      setSpecificMoodSelections(prev => {
        const newSelections = { ...prev };
        delete newSelections[trackId];
        return newSelections;
      });
      
      // Remove from updated tracks
      setUpdatedTracks(prev => {
        const newTracks = { ...prev };
        delete newTracks[trackId];
        return newTracks;
      });
      
      return;
    }
    
    // Otherwise, set the primary mood
    setPrimaryMoodSelections(prev => ({
      ...prev,
      [trackId]: mood
    }));
    
    // Reset specific mood when primary changes
    setSpecificMoodSelections(prev => {
      const newSelections = { ...prev };
      delete newSelections[trackId];
      return newSelections;
    });
    
    // Update the combined mood
    setUpdatedTracks(prev => ({
      ...prev,
      [trackId]: mood
    }));
  };
  
  // Function to handle specific mood updates
  const handleSpecificMoodChange = (trackId: number, specificMood: string) => {
    // If specific mood is empty or default, just use the primary mood
    if (!specificMood) {
      setSpecificMoodSelections(prev => {
        const newSelections = { ...prev };
        delete newSelections[trackId];
        return newSelections;
      });
      
      // Update with just the primary mood
      const primaryMood = primaryMoodSelections[trackId] ?? 'neutral';
      if (primaryMood) {
        setUpdatedTracks(prev => ({
          ...prev,
          [trackId]: primaryMood
        }));
      }
      
      return;
    }
    
    // Otherwise set the specific mood
    setSpecificMoodSelections(prev => ({
      ...prev,
      [trackId]: specificMood
    }));
    
    // Update the combined mood with primary + specific
    const primaryMood = primaryMoodSelections[trackId] ?? 'neutral';
    setUpdatedTracks(prev => ({
      ...prev,
      [trackId]: `${primaryMood}: ${specificMood}`
    }));
  };
  
  // Function to handle visibility toggles
  const handleVisibilityChange = (trackId: number, isVisible: boolean) => {
    setVisibilitySelections(prev => ({
      ...prev,
      [trackId]: isVisible
    }));
    // Note: Actual saving to backend will be implemented later
  };
  
  // Get specific moods available based on primary mood selection
  const getAvailableSpecificMoods = (trackId: number): string[] => {
    const primaryMood = primaryMoodSelections[trackId] ?? 'neutral';
    if (!primaryMood) return [];
    
    // Find category and specific moods
    for (const category in moodCategories) {
      const categoryMoods = moodCategories[category as keyof typeof moodCategories];
      if (primaryMood in categoryMoods) {
        return categoryMoods[primaryMood as keyof typeof categoryMoods];
      }
    }
    
    return [];
  };
  
  // Save updates to localStorage
  useEffect(() => {
    const saveToLocalStorage = () => {
      try {
        localStorage.setItem('updatedTrackMoods', JSON.stringify(updatedTracks));
        localStorage.setItem('primaryMoodSelections', JSON.stringify(primaryMoodSelections));
        localStorage.setItem('specificMoodSelections', JSON.stringify(specificMoodSelections));
        localStorage.setItem('visibilitySelections', JSON.stringify(visibilitySelections));
      } catch (e) {
        console.error("Error saving to localStorage:", e);
      }
    };
    
    // Debounce the save operation
    const timeoutId = setTimeout(saveToLocalStorage, 500);
    return () => clearTimeout(timeoutId);
  }, [updatedTracks, primaryMoodSelections, specificMoodSelections, visibilitySelections]);
  
  // Load saved data from localStorage
  useEffect(() => {
    try {
      const savedUpdatedTracks = localStorage.getItem('updatedTrackMoods');
      const savedPrimaryMoods = localStorage.getItem('primaryMoodSelections');
      const savedSpecificMoods = localStorage.getItem('specificMoodSelections');
      const savedVisibility = localStorage.getItem('visibilitySelections');
      
      if (savedUpdatedTracks) {
        setUpdatedTracks(JSON.parse(savedUpdatedTracks));
      }
      
      if (savedPrimaryMoods) {
        setPrimaryMoodSelections(JSON.parse(savedPrimaryMoods));
      }
      
      if (savedSpecificMoods) {
        setSpecificMoodSelections(JSON.parse(savedSpecificMoods));
      }
      
      if (savedVisibility) {
        setVisibilitySelections(JSON.parse(savedVisibility));
      }
    } catch (e) {
      console.error("Error loading from localStorage:", e);
    }
  }, []);
  
  // Get filtered and sorted tracks
  const getFilteredTracks = (): TrackWithMood[] => {
    if (!data?.tracks) return [];
    
    // First, combine tracks with their mood data
    const tracksWithMoods: TrackWithMood[] = data.tracks.map(track => ({
      ...track,
      updatedMood: updatedTracks[track.id] || "",
      primaryMood: primaryMoodSelections[track.id] ?? 'neutral',
      specificMood: specificMoodSelections[track.id] || "",
      originalMood: track.mood || "",
      visible: visibilitySelections[track.id] ?? track.visible
    }));
    
    // Apply search filter
    let filtered = tracksWithMoods;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(track => 
        track.title.toLowerCase().includes(query) ||
        (track.mood && track.mood.toLowerCase().includes(query)) ||
        (track.updatedMood && track.updatedMood.toLowerCase().includes(query))
      );
    }
    
    // Filter by updated status
    if (filterByUpdated) {
      filtered = filtered.filter(track => !!track.updatedMood);
    }
    
    // Filter by category
    if (filterCategory) {
      filtered = filtered.filter(track => {
        // Check if primary mood is in this category
        const categoryMoods = moodCategories[filterCategory as keyof typeof moodCategories];
        return !!categoryMoods && track.primaryMood in categoryMoods;
      });
    }
    
    // Apply sorting
    return filtered.sort((a, b) => {
      let valueA: string | number = '';
      let valueB: string | number = '';
      
      if (sortField === 'title') {
        valueA = a.title;
        valueB = b.title;
      } else if (sortField === 'bpm') {
        valueA = a.bpm || 0;
        valueB = b.bpm || 0;
      } else if (sortField === 'mood') {
        valueA = a.updatedMood || a.mood || '';
        valueB = b.updatedMood || b.mood || '';
      }
      
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortOrder === 'asc' 
          ? valueA.localeCompare(valueB) 
          : valueB.localeCompare(valueA);
      } else {
        return sortOrder === 'asc' 
          ? (valueA as number) - (valueB as number) 
          : (valueB as number) - (valueA as number);
      }
    });
  };
  
  const filteredTracks = getFilteredTracks();
  
  // Calculate progress stats
  const totalTracks = data?.tracks?.length || 0;
  const updatedCount = Object.keys(updatedTracks).length;
  const progressPercentage = totalTracks > 0 ? Math.round((updatedCount / totalTracks) * 100) : 0;
  
  // Export data as CSV
  const exportAsCSV = () => {
    if (!data?.tracks) return;
    
    const tracksWithMoods = data.tracks.map(track => ({
      id: track.id,
      title: track.title,
      originalMood: track.mood || "",
      updatedMood: updatedTracks[track.id] || "",
      bpm: track.bpm || ""
    }));
    
    // Create CSV content
    const headers = ["ID", "Title", "Original Mood", "Updated Mood", "BPM"];
    let csvContent = headers.join(",") + "\n";
    
    tracksWithMoods.forEach(track => {
      // Escape commas and quotes in the data
      const row = [
        track.id,
        `"${track.title.replace(/"/g, '""')}"`,
        `"${track.originalMood.replace(/"/g, '""')}"`,
        `"${track.updatedMood.replace(/"/g, '""')}"`,
        track.bpm
      ];
      csvContent += row.join(",") + "\n";
    });
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'track_moods.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Export Successful",
      description: `Exported ${tracksWithMoods.length} tracks to CSV`,
    });
  };
  
  // Get category for a primary mood
  const getCategoryForMood = (mood: string): string | null => {
    for (const category in moodCategories) {
      if (mood in moodCategories[category as keyof typeof moodCategories]) {
        return category;
      }
    }
    return null;
  };
  
  // Clear all mood selections
  const clearAllSelections = () => {
    if (confirm("Are you sure you want to clear all mood selections? This cannot be undone.")) {
      setUpdatedTracks({});
      setPrimaryMoodSelections({});
      setSpecificMoodSelections({});
      localStorage.removeItem('updatedTrackMoods');
      localStorage.removeItem('primaryMoodSelections');
      localStorage.removeItem('specificMoodSelections');
      
      toast({
        title: "Selections Cleared",
        description: "All mood selections have been cleared.",
      });
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div 
        className="mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Mood Editor</h1>
            <p className="text-muted-foreground max-w-2xl">
              Systematically update the mood classification for all tracks. Your progress is 
              automatically saved to your browser.
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className={updatedCount > 0 ? "bg-primary/10 text-primary" : ""}>
              {updatedCount} / {totalTracks} Tracks Updated ({progressPercentage}%)
            </Badge>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              type="text"
              placeholder="Search by track title or mood..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select 
            value={filterCategory || "default"}
            onValueChange={(value) => setFilterCategory(value === "default" ? null : value)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">All Categories</SelectItem>
              {Object.keys(moodCategories).map(category => (
                <SelectItem key={category} value={category}>{category}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            className={filterByUpdated ? "bg-primary/10 text-primary" : ""}
            onClick={() => setFilterByUpdated(!filterByUpdated)}
          >
            <Filter className="w-4 h-4 mr-2" />
            {filterByUpdated ? "Showing Updated" : "Show All"}
          </Button>
          
          <Button 
            variant="outline" 
            onClick={() => {
              setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
            }}
          >
            <ArrowUpDown className="w-4 h-4 mr-2" />
            {sortOrder === 'asc' ? "A to Z" : "Z to A"}
          </Button>
          
          <Select 
            value={sortField}
            onValueChange={(value) => setSortField(value as 'title' | 'bpm' | 'mood')}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="title">Sort by Title</SelectItem>
              <SelectItem value="bpm">Sort by BPM</SelectItem>
              <SelectItem value="mood">Sort by Mood</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex flex-wrap gap-2 mb-6">
          <Button onClick={exportAsCSV}>
            <Download className="w-4 h-4 mr-2" />
            Export as CSV
          </Button>
          
          <Button variant="destructive" onClick={clearAllSelections}>
            Clear All Selections
          </Button>
        </div>
        
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="mt-4 text-muted-foreground">Loading tracks...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-destructive">
            <p>Error loading tracks. Please try refreshing the page.</p>
            <Button onClick={() => refetch()} className="mt-4">
              Retry
            </Button>
          </div>
        ) : (
          <div>
            {/* Tabs for different views */}
            <Tabs defaultValue="table" className="mb-6">
              <TabsList>
                <TabsTrigger value="table">Table View</TabsTrigger>
                <TabsTrigger value="cards">Card View</TabsTrigger>
              </TabsList>
              
              <TabsContent value="table" className="mt-6">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-border text-sm text-muted-foreground">
                        <th className="text-left py-2 px-4">#</th>
                        <th className="text-left py-2 px-4">Title</th>
                        <th className="text-left py-2 px-4">BPM</th>
                        <th className="text-left py-2 px-4">Original Mood</th>
                        <th className="text-left py-2 px-4">Primary Mood</th>
                        <th className="text-left py-2 px-4">Specific Mood</th>
                        <th className="text-left py-2 px-4">New Mood</th>
                        <th className="text-left py-2 px-4">Visible</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTracks.map((track, index) => (
                        <tr key={track.id} className="border-b border-border hover:bg-primary/5">
                          <td className="py-3 px-4">{index + 1}</td>
                          <td className="py-3 px-4 font-medium">{track.title}</td>
                          <td className="py-3 px-4">{track.bpm || "-"}</td>
                          <td className="py-3 px-4 text-muted-foreground">{track.mood || "-"}</td>
                          <td className="py-3 px-4">
                            <Select 
                              value={primaryMoodSelections[track.id] ?? 'neutral'}
                              onValueChange={(value) => handlePrimaryMoodChange(track.id, value === "default" ? "" : value)}
                            >
                              <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Select primary mood" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="default">Select mood</SelectItem>
                                {primaryMoods.map(mood => (
                                  <SelectItem key={mood} value={mood}>{mood}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-3 px-4">
                            <Select 
                              value={specificMoodSelections[track.id] || "default"}
                              onValueChange={(value) => handleSpecificMoodChange(track.id, value === "default" ? "" : value)}
                              disabled={!primaryMoodSelections[track.id]}
                            >
                              <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Select specific mood" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="default">Select specific mood</SelectItem>
                                {getAvailableSpecificMoods(track.id).map(mood => (
                                  <SelectItem key={mood} value={mood}>{mood}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-3 px-4">
                            {updatedTracks[track.id] ? (
                              <Badge className="bg-primary/10 text-primary">
                                {updatedTracks[track.id]}
                                <Check className="ml-1 w-3 h-3" />
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">Not updated</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center space-x-2">
                              <Switch 
                                id={`visible-${track.id}`}
                                checked={visibilitySelections[track.id] ?? track.visible}
                                onCheckedChange={(checked) => handleVisibilityChange(track.id, checked)}
                              />
                              <Label htmlFor={`visible-${track.id}`} className="text-sm text-gray-400">
                                {visibilitySelections[track.id] ?? track.visible ? 'Visible' : 'Hidden'}
                              </Label>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
              
              <TabsContent value="cards" className="mt-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredTracks.map(track => (
                    <motion.div 
                      key={track.id}
                      className="border border-border rounded-lg overflow-hidden bg-card"
                      whileHover={{ y: -5, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Music className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-medium">{track.title}</h3>
                            <p className="text-xs text-muted-foreground">BPM: {track.bpm || "Unknown"}</p>
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <p className="text-xs font-medium text-muted-foreground mb-1">ORIGINAL MOOD</p>
                          <p className="text-sm">{track.mood || "None"}</p>
                        </div>
                        
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">PRIMARY MOOD</p>
                            <Select 
                              value={primaryMoodSelections[track.id] ?? 'neutral'}
                              onValueChange={(value) => handlePrimaryMoodChange(track.id, value === "default" ? "" : value)}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select primary mood" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="default">Select mood</SelectItem>
                                {primaryMoods.map(mood => (
                                  <SelectItem key={mood} value={mood}>{mood}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">SPECIFIC MOOD</p>
                            <Select 
                              value={specificMoodSelections[track.id] || "default"}
                              onValueChange={(value) => handleSpecificMoodChange(track.id, value === "default" ? "" : value)}
                              disabled={!primaryMoodSelections[track.id]}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select specific mood" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="default">Select specific mood</SelectItem>
                                {getAvailableSpecificMoods(track.id).map(mood => (
                                  <SelectItem key={mood} value={mood}>{mood}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">UPDATED MOOD</p>
                            {updatedTracks[track.id] ? (
                              <Badge className="bg-primary/10 text-primary w-full justify-center py-1 h-9">
                                {updatedTracks[track.id]}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="w-full justify-center py-1 h-9 text-muted-foreground">
                                Not updated
                              </Badge>
                            )}
                          </div>
                          
                          {primaryMoodSelections[track.id] && getCategoryForMood(primaryMoodSelections[track.id]) && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground mb-1">CATEGORY</p>
                              <Badge variant="outline" className="bg-muted/30">
                                {getCategoryForMood(primaryMoodSelections[track.id])}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </motion.div>
    </div>
  );
} 